/**
 * Orbis Output Shell v1 — leere .docx für „Neues Dokument“ / ARKO-Synthese.
 *
 * Kanon: design-pack/BRAND.md → Ausgabe-CI / Output Shell v1
 * (gleicher Wortlaut wie scripts/labor/output-shell/html-shell.js).
 *
 * OnlyOffice-kompatibel, ohne Extra-Dependency (OOXML-ZIP STORE).
 * Kanzlei-Defaults: Calibri 12 pt, de-DE, A4, Ränder 2,5 cm L/R.
 */

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Kanon Co-Brand Zeile 1 (BRAND.md / html-shell.js). Orbis ohne ®. */
export const ORBIS_HEADER_BRAND_LINE = 'ARKORBIT® · Orbis';

/** Kanon Footer-Disclaimer (Recht_Marken_Brauneis 06.08.2026). */
export const ORBIS_FOOTER_DISCLAIMER =
  'Dieses Dokument wurde mit Orbis von ARKORBIT® als technisches Werkzeug KI-gestützt erstellt. ' +
  'Es stellt keine Rechtsberatung dar. KI-generierte Inhalte ersetzen keine anwaltliche Beratung. ' +
  'Die fachliche und rechtliche Verantwortung sowie die Prüfpflicht für alle Inhalte verbleiben ' +
  'ausschließlich bei der beauftragten Rechtsanwältin / dem beauftragten Rechtsanwalt bzw. der Kanzlei. ' +
  'ARKORBIT haftet nicht für die inhaltliche Richtigkeit KI-gestützt erzeugter Texte (vgl. AGB § 5). ' +
  'Hinweis zur anwaltlichen Verschwiegenheit: § 9 RAO.';

/** Fallback wie PDF-Shell (html-shell.js), keine echten Kanzlei-PII. */
export const ORBIS_DEFAULT_KANZLEI_NAME = 'Musterkanzlei Demo';

export type BlankDocxOptions = {
  /** Tenant-Anzeigename → Co-Brand „… für {KANZLEI_NAME}“. */
  kanzleiName?: string;
  /** Fließtext als Absätze in document.xml (Schreiben-Export). Leer = leeres Blatt. */
  bodyText?: string;
};

const blankDocxCache = new Map<string, Uint8Array>();

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Kanon Co-Brand-Zeile (BRAND.md): Erstellt mit Orbis von ARKORBIT® für {KANZLEI}. */
export function buildOrbisCobrandLine(kanzleiName?: string): string {
  const name = String(kanzleiName ?? '').trim() || ORBIS_DEFAULT_KANZLEI_NAME;
  return `Erstellt mit Orbis von ARKORBIT® für ${name.slice(0, 120)}`;
}

function resolveKanzleiKey(kanzleiName?: string): string {
  return String(kanzleiName ?? '').trim() || ORBIS_DEFAULT_KANZLEI_NAME;
}

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

/** CRC-32 (ZIP). */
function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    c ^= data[i];
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? (c >>> 1) ^ 0xedb88320 : c >>> 1;
    }
  }
  return (c ^ 0xffffffff) >>> 0;
}

function u16(n: number): Uint8Array {
  const b = new Uint8Array(2);
  new DataView(b.buffer).setUint16(0, n >>> 0, true);
  return b;
}

function u32(n: number): Uint8Array {
  const b = new Uint8Array(4);
  new DataView(b.buffer).setUint32(0, n >>> 0, true);
  return b;
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  let len = 0;
  for (const p of parts) len += p.length;
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/** Minimales ZIP (STORE, UTF-8-Namen). */
function zipStore(files: { name: string; data: Uint8Array }[]): Uint8Array {
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = utf8(file.name);
    const data = file.data;
    const crc = crc32(data);
    const localHeader = concatBytes([
      u32(0x04034b50),
      u16(20),
      u16(0x0800),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
    ]);
    localParts.push(localHeader, data);

    const central = concatBytes([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0x0800),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes,
    ]);
    centralParts.push(central);
    offset += localHeader.length + data.length;
  }

  const centralDir = concatBytes(centralParts);
  const end = concatBytes([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralDir.length),
    u32(offset),
    u16(0),
  ]);

  return concatBytes([...localParts, centralDir, end]);
}

function contentTypesXml(): string {
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' +
    '<Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>' +
    '<Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>' +
    '<Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>' +
    '<Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/>' +
    '</Types>'
  );
}

function rootRelsXml(): string {
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>' +
    '</Relationships>'
  );
}

function documentRelsXml(): string {
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
    '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>' +
    '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/>' +
    '<Relationship Id="rId4" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/>' +
    '</Relationships>'
  );
}

function stylesXml(): string {
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
    '<w:docDefaults>' +
    '<w:rPrDefault><w:rPr>' +
    '<w:rFonts w:ascii="Calibri" w:hAnsi="Calibri" w:cs="Calibri"/>' +
    '<w:sz w:val="24"/><w:szCs w:val="24"/>' +
    '<w:lang w:val="de-DE" w:eastAsia="de-DE" w:bidi="ar-SA"/>' +
    '</w:rPr></w:rPrDefault>' +
    '<w:pPrDefault><w:pPr><w:spacing w:after="0" w:line="276" w:lineRule="auto"/></w:pPr></w:pPrDefault>' +
    '</w:docDefaults>' +
    '</w:styles>'
  );
}

function settingsXml(): string {
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
    '<w:defaultTabStop w:val="708"/>' +
    '<w:characterSpacingControl w:val="doNotCompress"/>' +
    '</w:settings>'
  );
}

const BODY_RUN_PR =
  '<w:rPr>' +
  '<w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>' +
  '<w:sz w:val="24"/><w:lang w:val="de-DE"/>' +
  '</w:rPr>';

const SCHREIBEN_BODY_MAX_CHARS = 80000;

function paragraphXml(line: string): string {
  const inner = line
    ? `<w:r>${BODY_RUN_PR}<w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r>`
    : `<w:r>${BODY_RUN_PR}</w:r>`;
  return `<w:p><w:pPr>${BODY_RUN_PR}</w:pPr>${inner}</w:p>`;
}

function bodyContentXml(bodyText?: string): string {
  if (!bodyText) return paragraphXml('');
  const capped =
    bodyText.length > SCHREIBEN_BODY_MAX_CHARS ? bodyText.slice(0, SCHREIBEN_BODY_MAX_CHARS) : bodyText;
  return capped.replace(/\r\n/g, '\n').split('\n').map(paragraphXml).join('');
}

function documentXml(bodyText?: string): string {
  // A4: 11906×16838 twips. L/R 2,5 cm (1417). Oben 1,65 cm (935).
  // Unten großzügiger für Disclaimer-Seitenfuß (~35 mm / 1985 twips).
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" ' +
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    '<w:body>' +
    bodyContentXml(bodyText) +
    '<w:sectPr>' +
    '<w:headerReference w:type="default" r:id="rId3"/>' +
    '<w:footerReference w:type="default" r:id="rId4"/>' +
    '<w:pgSz w:w="11906" w:h="16838"/>' +
    '<w:pgMar w:top="935" w:right="1417" w:bottom="1985" w:left="1417" w:header="568" w:footer="720"/>' +
    '<w:docGrid w:linePitch="360"/>' +
    '</w:sectPr>' +
    '</w:body></w:document>'
  );
}

function headerXml(kanzleiName: string): string {
  const brand = escapeXml(ORBIS_HEADER_BRAND_LINE);
  const cobrand = escapeXml(buildOrbisCobrandLine(kanzleiName));
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
    '<w:p>' +
    '<w:pPr>' +
    '<w:pBdr><w:bottom w:val="single" w:sz="6" w:space="4" w:color="E2E8F0"/></w:pBdr>' +
    '<w:spacing w:after="40"/>' +
    '</w:pPr>' +
    '<w:r><w:rPr>' +
    '<w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>' +
    '<w:b/><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="111827"/>' +
    '</w:rPr><w:t>' +
    brand +
    '</w:t></w:r>' +
    '</w:p>' +
    '<w:p>' +
    '<w:pPr><w:spacing w:after="80"/></w:pPr>' +
    '<w:r><w:rPr>' +
    '<w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>' +
    '<w:b/><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="111827"/>' +
    '</w:rPr><w:t xml:space="preserve">' +
    cobrand +
    '</w:t></w:r>' +
    '</w:p>' +
    '</w:hdr>'
  );
}

/** Footer mit Kanon-Disclaimer; fette Hervorhebung wie PDF-Shell (KI-gestützt / keine Rechtsberatung). */
function footerXml(): string {
  const parts: { text: string; bold?: boolean }[] = [
    { text: 'Dieses Dokument wurde mit Orbis von ARKORBIT® als technisches Werkzeug ' },
    { text: 'KI-gestützt', bold: true },
    { text: ' erstellt. Es stellt ' },
    { text: 'keine Rechtsberatung', bold: true },
    {
      text:
        ' dar. KI-generierte Inhalte ersetzen keine anwaltliche Beratung. ' +
        'Die fachliche und rechtliche Verantwortung sowie die Prüfpflicht für alle Inhalte verbleiben ' +
        'ausschließlich bei der beauftragten Rechtsanwältin / dem beauftragten Rechtsanwalt bzw. der Kanzlei. ' +
        'ARKORBIT haftet nicht für die inhaltliche Richtigkeit KI-gestützt erzeugter Texte (vgl. AGB § 5). ' +
        'Hinweis zur anwaltlichen Verschwiegenheit: § 9 RAO.',
    },
  ];

  const runs = parts
    .map((p) => {
      const bold = p.bold ? '<w:b/>' : '';
      const color = p.bold ? '111827' : '64748B';
      return (
        '<w:r><w:rPr>' +
        '<w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>' +
        bold +
        `<w:sz w:val="14"/><w:szCs w:val="14"/><w:color w:val="${color}"/>` +
        '</w:rPr><w:t xml:space="preserve">' +
        escapeXml(p.text) +
        '</w:t></w:r>'
      );
    })
    .join('');

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<w:ftr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
    '<w:p>' +
    '<w:pPr>' +
    '<w:pBdr><w:top w:val="single" w:sz="4" w:space="4" w:color="E2E8F0"/></w:pBdr>' +
    '<w:spacing w:before="40"/>' +
    '</w:pPr>' +
    runs +
    '</w:p>' +
    '</w:ftr>'
  );
}

function buildBlankDocxBytes(kanzleiName: string, bodyText?: string): Uint8Array {
  return zipStore([
    { name: '[Content_Types].xml', data: utf8(contentTypesXml()) },
    { name: '_rels/.rels', data: utf8(rootRelsXml()) },
    { name: 'word/document.xml', data: utf8(documentXml(bodyText)) },
    { name: 'word/_rels/document.xml.rels', data: utf8(documentRelsXml()) },
    { name: 'word/styles.xml', data: utf8(stylesXml()) },
    { name: 'word/settings.xml', data: utf8(settingsXml()) },
    { name: 'word/header1.xml', data: utf8(headerXml(kanzleiName)) },
    { name: 'word/footer1.xml', data: utf8(footerXml()) },
  ]);
}

function blankDocxByteArray(kanzleiName?: string): Uint8Array {
  const key = resolveKanzleiKey(kanzleiName);
  let bytes = blankDocxCache.get(key);
  if (!bytes) {
    bytes = buildBlankDocxBytes(key);
    blankDocxCache.set(key, bytes);
  }
  return bytes;
}

/** Vorschlagsname für ein neues leeres Word-Dokument (de-AT). */
export function suggestBlankDocxFilename(now = new Date()): string {
  const date = now.toLocaleDateString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = now
    .toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })
    .replace(':', '-');
  return `Neues Dokument ${date} ${time}.docx`;
}

/**
 * Erzeugt eine leere .docx mit Orbis Co-Brand-Header + Disclaimer-Seitenfuß
 * für den bestehenden PORTAL_DOCUMENT_UPLOAD-Pfad.
 */
export function createBlankDocxFile(
  filename = suggestBlankDocxFilename(),
  options?: BlankDocxOptions,
): File {
  const bodyText = typeof options?.bodyText === 'string' && options.bodyText.length > 0
    ? options.bodyText
    : undefined;
  const bytes = bodyText
    ? buildBlankDocxBytes(resolveKanzleiKey(options?.kanzleiName), bodyText)
    : blankDocxByteArray(options?.kanzleiName);
  const blob = new Blob([bytes], { type: DOCX_MIME });
  return new File([blob], filename, { type: DOCX_MIME, lastModified: Date.now() });
}
